/**
 * Mobile menu drawer — "BESTSELLERS" horizontal product row (VARIANT 1)
 *
 * Purely additive: inserts a new scrollable product row at the BOTTOM of the
 * navigation menu — right after the menu list, under the last link ("Guides").
 * No existing element is removed, restyled or reordered; every menu link stays
 * intact and clickable.
 *
 * Store-wide bestsellers (no species split): products come from the shop's
 * "Top sellers" recommendation recipe, which is ranked weekly from real Shopify
 * sales and needs no product context, so it works anywhere the menu opens.
 */
(function () {
  "use strict";

  /* ------------------------------------------------------------------ *
   * Experiment identifiers (resolved from the Variant runtime if present)
   * ------------------------------------------------------------------ */
  var V = typeof window !== "undefined" ? window.Variant : null;
  var W = typeof window !== "undefined" ? window : {};
  var experiment_id =
    (V && (V.experiment_id || V.experimentId)) ||
    (V && V.context && V.context.experiment_id) ||
    W.variantExperimentId ||
    "EXPD97C1B67";
  var variation_id =
    (V && (V.variation_id || V.variationId)) ||
    (V && V.context && V.context.variation_id) ||
    W.variantVariationId ||
    "1";

  // Only report when the runtime actually supplied the experiment identifiers.
  function hasIds() {
    return !!experiment_id && !!variation_id;
  }

  function reportFailure(reason, selector, description, errorMessage) {
    try {
      if (hasIds() && V && typeof V.reportExperienceFailure === "function") {
        V.reportExperienceFailure(
          reason,
          selector,
          experiment_id,
          variation_id,
          description,
          errorMessage,
        );
      }
    } catch (e) {
      /* never let monitoring break the page */
    }
  }

  function track(name, extra) {
    try {
      if (hasIds() && V && typeof V.publishCustomEvent === "function") {
        var payload = {
          experiment_id: experiment_id,
          variation_id: variation_id,
        };
        if (extra) {
          for (var k in extra) {
            if (Object.prototype.hasOwnProperty.call(extra, k))
              payload[k] = extra[k];
          }
        }
        V.publishCustomEvent(name, payload);
      }
    } catch (e) {
      /* ignore */
    }
  }

  function reportExposure(trigger, description) {
    try {
      if (hasIds() && V && typeof V.reportExposure === "function") {
        V.reportExposure(experiment_id, variation_id, trigger, description);
      }
    } catch (e) {
      /* ignore */
    }
  }

  /* ------------------------------------------------------------------ *
   * Content
   * ------------------------------------------------------------------ */
  var SECTION_ID = "variant-drawer-bestsellers";
  var STYLE_ID = "variant-drawer-bestsellers-styles";
  var NAV_SELECTOR = "nav.menu-drawer__navigation";
  var HEADING = "BESTSELLERS";
  var REGION_LABEL = "Bestsellers";

  // Primary source: the shop's "Top sellers" recommendation recipe — real
  // sales-ranked bestsellers across the whole store, no product context needed.
  var RECIPE_ID = "48897bb2-f87e-4f3a-bcb6-793f21d5984c";
  // Backup source if the recipe returns nothing: the store's own "Bestsellers"
  // collection (handle verified: "bestsellers") read live from the storefront.
  var COLLECTION_HANDLE = "bestsellers";
  var PRODUCTS_TO_SHOW = 8;

  // Fail-safe list used ONLY if both the recipe and the live collection fail
  // (offline, blocked, empty response). Titles, images and prices below are
  // taken verbatim from the store's own homepage bestsellers carousel — no
  // fabricated products and no invented prices.
  var FALLBACK_PRODUCTS = [
    {
      title: "Raw Chicken 500g",
      href: "/products/chicken-formula-adult-500g-tub",
      img: "https://www.nutriment.co.uk/cdn/shop/files/100134_chicken_500g_1.png?v=1733858546&width=533",
      price: "£3.15",
    },
    {
      title: "Raw Boneless Beef 500g",
      href: "/products/boneless-beef-formula-adult-500g-tub",
      img: "https://www.nutriment.co.uk/cdn/shop/files/100141BonelessBeef500g.png?v=1736531823&width=533",
      price: "£3.49",
    },
    {
      title: "Raw Turkey 500g",
      href: "/products/turkey-formula-adult-500g-tub",
      img: "https://www.nutriment.co.uk/cdn/shop/files/100137Turkey500g.png?v=1736532486&width=533",
      price: "£3.35",
    },
    {
      title: "Raw Duck 500g",
      href: "/products/duck-formula-adult-500g-tub",
      img: "https://www.nutriment.co.uk/cdn/shop/files/100180Duck500g.png?v=1736532159&width=533",
      price: "£3.30",
    },
    {
      title: "Raw Lamb 500g",
      href: "/products/lamb-formula-adult-500g-tub",
      img: "https://www.nutriment.co.uk/cdn/shop/files/100224Lamb500g.png?v=1736532231&width=533",
      price: "£3.79",
    },
    {
      title: "Raw Chicken 1.4kg",
      href: "/products/chicken-formula-adult-1-4kg-chubb",
      img: "https://www.nutriment.co.uk/cdn/shop/files/100135Chicken1.4kg.png?v=1736532021&width=533",
      price: "£6.29",
    },
    {
      title: "Raw Turkey 1.4kg",
      href: "/products/turkey-formula-adult-1-4kg-chubb",
      img: "https://www.nutriment.co.uk/cdn/shop/files/100138Turkey1.4kg.png?v=1736532503&width=533",
      price: "£7.39",
    },
    {
      title: "Raw Boneless Beef 1.4kg",
      href: "/products/boneless-beef-formula-adult-1-4kg-chubb",
      img: "https://www.nutriment.co.uk/cdn/shop/files/100142_boneless_beef_1.4kg_1.png?v=1736953524&width=533",
      price: "£7.99",
    },
  ];

  /* ------------------------------------------------------------------ *
   * Live product data.
   * Primary: the "Top sellers" recommendation recipe (real sales ranking).
   * Backup:  the store's own "bestsellers" collection, read over a relative
   *          same-origin path so it works through the preview proxy and on
   *          the live storefront alike.
   * ------------------------------------------------------------------ */
  function formatPrice(value) {
    // products.json returns the price as a decimal string in the shop currency.
    var n = parseFloat(value);
    if (isNaN(n)) return "";
    return "£" + n.toFixed(2); // £X.XX
  }

  // Recommendation payloads express price either as a decimal string in the
  // shop currency or as an integer number of pence. Only ever render a price we
  // actually received — never guess one.
  function priceFromRecommendation(p) {
    var raw =
      p && (p.price !== undefined && p.price !== null ? p.price : p.price_min);
    if (raw === undefined || raw === null || raw === "") return "";
    if (typeof raw === "number") {
      // Integers are pence (Shopify's money-in-cents convention).
      return Number.isInteger(raw) ? formatPrice(raw / 100) : formatPrice(raw);
    }
    var s = String(raw).trim();
    // Already-formatted money string ("£3.15") — pass straight through.
    if (/[£$€]/.test(s)) return s;
    if (/^\d+$/.test(s)) return formatPrice(parseInt(s, 10) / 100);
    return formatPrice(s);
  }

  function mapRecommendation(p) {
    try {
      if (!p) return null;
      var handle = p.handle || (p.url ? handleOf(p.url) : "");
      if (!handle) return null;
      var image =
        p.image ||
        p.featured_image_url ||
        p.featured_image ||
        (p.images && p.images[0]) ||
        "";
      if (image && image.src) image = image.src;
      var title = p.title || "";
      var price = priceFromRecommendation(p);
      if (!image || !title || !price) return null; // never render a half-empty card
      return {
        title: title,
        href: "/products/" + handle,
        img: image,
        price: price,
      };
    } catch (e) {
      return null;
    }
  }

  // Ask the Variant runtime for the shop's top sellers. Resolves to a mapped
  // list, or null so the caller can fall back to the collection.
  function fetchFromRecipe(callback) {
    var settled = false;
    function done(list) {
      if (settled) return;
      settled = true;
      callback(list);
    }
    var timer = setTimeout(function () {
      done(null);
    }, 3000);

    try {
      if (!V || typeof V.getRecommendations !== "function") {
        clearTimeout(timer);
        done(null);
        return;
      }
      Promise.resolve(
        V.getRecommendations({ recipeId: RECIPE_ID, limit: PRODUCTS_TO_SHOW }),
      )
        .then(function (res) {
          clearTimeout(timer);
          var raw = (res && (res.products || res.items)) || res || [];
          if (!Array.isArray(raw)) raw = [];
          var mapped = [];
          for (
            var i = 0;
            i < raw.length && mapped.length < PRODUCTS_TO_SHOW;
            i++
          ) {
            var m = mapRecommendation(raw[i]);
            if (m) mapped.push(m);
          }
          done(mapped.length ? mapped : null);
        })
        .catch(function () {
          clearTimeout(timer);
          done(null); // fail open — the caller tries the collection next
        });
    } catch (e) {
      clearTimeout(timer);
      done(null);
    }
  }

  function mapProduct(prod) {
    try {
      if (!prod || !prod.handle) return null;
      var variant = prod.variants && prod.variants[0] ? prod.variants[0] : null;
      var image =
        (prod.images &&
          prod.images[0] &&
          (prod.images[0].src || prod.images[0])) ||
        (prod.featured_image &&
          (prod.featured_image.src || prod.featured_image)) ||
        "";
      return {
        title: prod.title || "",
        href: "/products/" + prod.handle,
        img: image,
        price: variant ? formatPrice(variant.price) : "",
      };
    } catch (e) {
      return null;
    }
  }

  // Backup: read the store's own "bestsellers" collection.
  function fetchFromCollection(callback) {
    var url =
      "/collections/" +
      COLLECTION_HANDLE +
      "/products.json?limit=" +
      PRODUCTS_TO_SHOW;
    var settled = false;
    function done(list) {
      if (settled) return;
      settled = true;
      callback(list);
    }
    // Guard: never let a slow/hung request block the row — fall back after 3s.
    var timer = setTimeout(function () {
      done(null);
    }, 3000);

    try {
      fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
        .then(function (res) {
          if (!res || !res.ok) {
            reportFailure(
              "api_error",
              url,
              "load the live bestsellers collection products for the mobile menu row",
              JSON.stringify({
                route: url,
                status: res ? res.status : "no-response",
              }),
            );
            throw new Error("bad response");
          }
          return res.json();
        })
        .then(function (data) {
          clearTimeout(timer);
          var raw = (data && data.products) || [];
          var mapped = [];
          for (
            var i = 0;
            i < raw.length && mapped.length < PRODUCTS_TO_SHOW;
            i++
          ) {
            var m = mapProduct(raw[i]);
            if (m && m.img && m.title) mapped.push(m);
          }
          done(mapped.length ? mapped : null);
        })
        .catch(function () {
          clearTimeout(timer);
          done(null); // fail open — the caller uses the fallback list
        });
    } catch (e) {
      clearTimeout(timer);
      done(null);
    }
  }

  // Top sellers recipe first; the bestsellers collection if that comes back
  // empty; the caller falls back to FALLBACK_PRODUCTS if both do.
  function fetchBestsellers(callback) {
    fetchFromRecipe(function (fromRecipe) {
      if (fromRecipe && fromRecipe.length) {
        callback(fromRecipe);
        return;
      }
      fetchFromCollection(callback);
    });
  }

  /* ------------------------------------------------------------------ *
   * Styles — matched to the homepage "bestsellers" carousel.
   * The homepage cards use the theme's dark card colour scheme
   * (background #454142, white text, orange #ffa400 accent) with a light
   * image tile and generously rounded corners (card_corner_radius: 20).
   * We reuse the same palette and card look here so the drawer row reads as
   * the same component. DM Sans body font is inherited from the drawer.
   * ------------------------------------------------------------------ */
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var head = document.head || document.getElementsByTagName("head")[0];
    if (!head) return;
    var style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = [
      "#" + SECTION_ID + "{",
      "  --vbs-inset:0px;", // nav already insets us; no extra indent so we align with the menu links
      "  --vbs-pad:23px;", // the nav's own horizontal padding (read live) — used to cap our width
      "  --vbs-card-bg:#454142;", // homepage bestsellers card scheme background
      "  --vbs-card-text:#ffffff;", // homepage card text colour
      "  --vbs-accent:#ffa400;", // homepage scheme accent (orange)
      "  --vbs-card-radius:16px;", // homepage card_corner_radius (20) scaled for the compact drawer card
      "  box-sizing:border-box;",
      // Hard-cap our width to the drawer's inner width (viewport minus both nav
      // paddings). This stops the fixed-width cards from stretching the whole
      // navigation menu wide — they scroll sideways inside this fixed track.
      "  width:calc(100vw - 2 * var(--vbs-pad));",
      "  max-width:calc(100vw - 2 * var(--vbs-pad));",
      "  min-width:0;",
      "  background-color:rgb(var(--color-background,255,255,255));",
      "  color:rgb(var(--color-foreground,80,82,84));",
      "  font-family:inherit;",
      // Sits at the BOTTOM of the menu, under "Guides": a hairline divider on
      // top (matching the theme list dividers) separates the row from the last
      // menu link above it, with breathing room above the heading.
      "  padding:1.6rem 0 0;margin:0.5rem 0 0;",
      "  border-top:1px solid rgba(0,0,0,0.15);",
      "}",
      "#" + SECTION_ID + " .variant-bs__heading{",
      "  display:block;",
      "  padding:0 var(--vbs-inset) 1.2rem;",
      "  margin:0;",
      // Same family/weight/style/colour as the theme categories (For Dogs...),
      // sized a step above them (2rem vs 1.8rem) so the section title reads as
      // a heading without dominating the menu.
      "  font-family:inherit;",
      "  font-size:2rem;",
      "  font-weight:700;",
      "  line-height:1.3;",
      "  letter-spacing:0.6px;",
      "  text-transform:uppercase;",
      "  color:rgb(var(--color-foreground,80,82,84));",
      "}",
      "#" + SECTION_ID + " .variant-bs__row{",
      "  display:flex;",
      "  flex-wrap:nowrap;",
      "  gap:12px;",
      "  align-items:stretch;",
      "  width:100%;",
      "  max-width:100%;",
      "  min-width:0;",
      "  overflow-x:auto;",
      "  overflow-y:hidden;",
      "  -webkit-overflow-scrolling:touch;",
      "  scroll-snap-type:x proximity;",
      "  padding:0 var(--vbs-inset);",
      "  margin:0;",
      "  scrollbar-width:none;",
      "  -ms-overflow-style:none;",
      "}",
      "#" +
        SECTION_ID +
        " .variant-bs__row::-webkit-scrollbar{display:none;height:0;}",
      // The card itself is the dark, rounded panel — like the homepage card.
      "#" + SECTION_ID + " .variant-bs__card{",
      "  flex:0 0 140px;",
      "  width:140px;",
      "  scroll-snap-align:start;",
      "  display:flex;",
      "  flex-direction:column;",
      "  background-color:var(--vbs-card-bg);",
      "  border:2px solid transparent;", // no visible card outline (kept transparent so sizing stays stable)
      "  border-radius:var(--vbs-card-radius);",
      "  overflow:hidden;",
      "  text-decoration:none;",
      "  color:var(--vbs-card-text);",
      "  -webkit-tap-highlight-color:transparent;",
      "}",
      "#" + SECTION_ID + " .variant-bs__card:focus-visible{",
      "  outline:2px solid var(--vbs-accent);",
      "  outline-offset:2px;",
      "}",
      // Light image tile inside the dark card (product shots read best on white).
      "#" + SECTION_ID + " .variant-bs__media{",
      "  display:block;",
      "  width:100%;",
      "  height:120px;",
      "  overflow:hidden;",
      "  background-color:#ffffff;",
      "}",
      "#" + SECTION_ID + " .variant-bs__media img{",
      "  display:block;",
      "  width:100%;",
      "  height:100%;",
      "  object-fit:contain;",
      "}",
      // Text block sits on the dark card, left-aligned (card_text_alignment: left).
      "#" + SECTION_ID + " .variant-bs__body{",
      "  display:flex;",
      "  flex-direction:column;",
      "  flex:1 1 auto;",
      "  padding:0.9rem 1rem 1.1rem;",
      "  text-align:left;",
      "}",
      "#" + SECTION_ID + " .variant-bs__title{",
      "  display:block;",
      "  margin:0;",
      "  font-family:inherit;",
      "  font-size:1.3rem;",
      "  font-weight:500;",
      "  line-height:1.3;",
      "  color:var(--vbs-card-text);",
      "  display:-webkit-box;",
      "  -webkit-line-clamp:2;",
      "  -webkit-box-orient:vertical;",
      "  overflow:hidden;",
      "}",
      "#" + SECTION_ID + " .variant-bs__price{",
      "  display:block;",
      "  margin:0.6rem 0 0;",
      "  font-family:inherit;",
      "  font-size:1.4rem;",
      "  font-weight:700;",
      "  line-height:1.2;",
      "  color:var(--vbs-card-text);",
      "}",
    ].join("\n");
    head.appendChild(style);
  }

  /* ------------------------------------------------------------------ *
   * Build the section
   * ------------------------------------------------------------------ */
  function handleOf(url) {
    try {
      var m = String(url).match(/\/products\/([^/?#]+)/);
      return m ? m[1] : url;
    } catch (e) {
      return url;
    }
  }

  function buildSection(inset, pad, products) {
    var list = products && products.length ? products : FALLBACK_PRODUCTS;
    var section = document.createElement("div");
    section.id = SECTION_ID;
    section.setAttribute("role", "region");
    section.setAttribute("aria-label", REGION_LABEL);
    if (inset) section.style.setProperty("--vbs-inset", inset);
    if (pad) section.style.setProperty("--vbs-pad", pad);

    var heading = document.createElement("span");
    heading.className = "variant-bs__heading";
    heading.id = SECTION_ID + "-heading";
    heading.textContent = HEADING;
    section.appendChild(heading);

    var row = document.createElement("div");
    row.className = "variant-bs__row";
    row.setAttribute("aria-labelledby", heading.id);

    list.forEach(function (p) {
      var card = document.createElement("a");
      card.className = "variant-bs__card";
      card.href = p.href;
      card.setAttribute("data-variant-bs-handle", handleOf(p.href));

      var media = document.createElement("span");
      media.className = "variant-bs__media";
      var img = document.createElement("img");
      img.src = p.img;
      img.alt = p.title;
      img.width = 140;
      img.height = 120;
      img.setAttribute("loading", "lazy");
      img.setAttribute("decoding", "async");
      media.appendChild(img);
      card.appendChild(media);

      var body = document.createElement("span");
      body.className = "variant-bs__body";

      var title = document.createElement("span");
      title.className = "variant-bs__title";
      title.textContent = p.title;
      body.appendChild(title);

      var price = document.createElement("span");
      price.className = "variant-bs__price";
      price.textContent = p.price;
      body.appendChild(price);

      card.appendChild(body);

      // Product click — fire attribution before navigating away
      card.addEventListener("click", function (e) {
        // Nothing to send, or the shopper is opening a new tab/window:
        // leave the browser's own navigation completely untouched.
        if (!hasIds()) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
          return;

        var destination = card.href;
        try {
          e.preventDefault();
        } catch (err) {
          return;
        }
        var safety = setTimeout(function () {
          window.location.href = destination;
        }, 2000);
        track("product-clicked-attribution", {
          product_id: handleOf(p.href),
          product_title: p.title,
          location: "mobile_menu_bestsellers",
        });
        clearTimeout(safety);
        window.location.href = destination;
      });

      row.appendChild(card);
    });

    // Swipe / scroll of the row — fire once
    var scrolled = false;
    row.addEventListener(
      "scroll",
      function () {
        if (scrolled || row.scrollLeft <= 4) return;
        scrolled = true;
        track("carousel_scrolled", { location: "mobile_menu_bestsellers" });
      },
      { passive: true },
    );

    section.appendChild(row);
    return section;
  }

  /* Reveal only once the product images are paintable (no pop-in),
     always with error + timeout fallbacks so it can never stay hidden. */
  function revealWhenImagesReady(section, products) {
    var list = products && products.length ? products : FALLBACK_PRODUCTS;
    var done = false;
    function reveal() {
      if (done) return;
      done = true;
      section.style.visibility = "";
    }
    section.style.visibility = "hidden";
    setTimeout(reveal, 2500); // safety net

    var pending = list.length;
    function settle() {
      pending -= 1;
      if (pending <= 0) reveal();
    }
    list.forEach(function (p) {
      var pre = new Image();
      var settledOnce = false;
      function once() {
        if (settledOnce) return;
        settledOnce = true;
        settle();
      }
      pre.onload = once;
      pre.onerror = once; // fail open
      pre.src = p.img;
    });
  }

  /* ------------------------------------------------------------------ *
   * Insert (idempotent, tolerant of a late-rendering drawer)
   * ------------------------------------------------------------------ */
  function tryApply() {
    if (document.getElementById(SECTION_ID)) return true;

    var nav = document.querySelector(NAV_SELECTOR);
    if (!nav) return false; // not rendered yet — the observer will retry

    try {
      injectStyles();

      // The nav itself already applies the theme's horizontal inset to its
      // children, so the section sits flush with the menu links. We therefore
      // add NO extra inset of our own (that was double-indenting the cards) —
      // heading and cards line up exactly with the "For Dogs / For Cats" text.
      var inset = "0px";

      // Read the nav's real horizontal padding (23px normally, less on short
      // viewports). We cap the section to (viewport - both nav paddings) so the
      // fixed-width cards can NEVER stretch the nav to their combined width —
      // instead they scroll horizontally inside a drawer-width track.
      var pad = "23px";
      try {
        var padLeft = window.getComputedStyle(nav).paddingLeft;
        if (padLeft && parseFloat(padLeft) >= 0) pad = padLeft;
      } catch (e) {
        /* keep the theme default */
      }

      // Fetch the live bestsellers (Top sellers recipe, then the bestsellers
      // collection) and build the row from
      // them. If the fetch fails or times out, fetchBestsellers hands back null
      // and buildSection falls back to the hardcoded FALLBACK_PRODUCTS so the
      // row always renders.
      fetchBestsellers(function (live) {
        // Guard against a race: the observer may have inserted the section
        // between the fetch kicking off and its callback firing.
        if (document.getElementById(SECTION_ID)) return;

        var nav2 = document.querySelector(NAV_SELECTOR);
        if (!nav2) return; // drawer went away before we could build

        try {
          var section = buildSection(inset, pad, live);
          revealWhenImagesReady(section, live);

          // BOTTOM placement: put the row below the menu list, i.e. under the
          // last link ("Guides"). Insert directly after the menu list so it
          // sits at the end of the navigation, matching the menu's natural
          // top-to-bottom order.
          var list = nav2.querySelector("ul.menu-drawer__menu");
          if (list && list.parentNode === nav2) {
            nav2.insertBefore(section, list.nextSibling);
          } else {
            nav2.appendChild(section);
          }
        } catch (err2) {
          reportFailure(
            "mutation_failed",
            NAV_SELECTOR,
            'add a scrollable BESTSELLERS product row at the bottom of the mobile menu, under the "Guides" link',
            err2 && err2.message ? err2.message : String(err2),
          );
        }
      });
      return true;
    } catch (err) {
      reportFailure(
        "mutation_failed",
        NAV_SELECTOR,
        'add a scrollable BESTSELLERS product row at the bottom of the mobile menu, under the "Guides" link',
        err && err.message ? err.message : String(err),
      );
      return true; // do not retry a throwing insert
    }
  }

  function start() {
    var applied = tryApply();

    var root = document.querySelector("#menu-drawer") || document.body;
    if (!root) return;

    var obs = null;
    var stopped = false;
    function stop() {
      if (stopped) return;
      stopped = true;
      if (obs) obs.disconnect();
      obs = null;
    }

    if (!applied) {
      obs = new MutationObserver(function () {
        try {
          if (tryApply()) stop();
        } catch (e) {
          stop();
        }
      });
      obs.observe(root, { childList: true, subtree: true });

      // Give up (and report) if the menu never renders
      setTimeout(function () {
        if (stopped) return;
        stop();
        if (!document.getElementById(SECTION_ID)) {
          reportFailure(
            "element_not_found",
            NAV_SELECTOR,
            "could not find the mobile menu drawer navigation to add the BESTSELLERS product row at its bottom",
          );
        }
      }, 10000);
    }
  }

  /* ------------------------------------------------------------------ *
   * Exposure detector — watches the THEME's own drawer panel becoming
   * visible (identical logic in variant_control.js)
   * ------------------------------------------------------------------ */
  function startExposure() {
    var panel = document.querySelector("#menu-drawer");
    if (!panel) {
      reportFailure(
        "element_not_found",
        "#menu-drawer",
        "exposure detector: watch the mobile navigation menu panel for opening",
      );
      return;
    }
    var fired = false;
    var obs = null;
    var poll = null;

    function isVisible() {
      try {
        if (typeof panel.checkVisibility === "function") {
          if (
            !panel.checkVisibility({
              checkOpacity: true,
              checkVisibilityCSS: true,
            })
          )
            return false;
        } else {
          var cs = window.getComputedStyle(panel);
          if (
            cs.display === "none" ||
            cs.visibility === "hidden" ||
            parseFloat(cs.opacity) === 0
          ) {
            return false;
          }
        }
        var r = panel.getBoundingClientRect();
        return (
          r.width > 0 &&
          r.height > 0 &&
          r.right > 0 &&
          r.left < window.innerWidth &&
          r.bottom > 0 &&
          r.top < window.innerHeight
        );
      } catch (e) {
        return false;
      }
    }
    function stop() {
      if (obs) obs.disconnect();
      if (poll) clearInterval(poll);
      obs = null;
      poll = null;
    }
    function check() {
      if (fired || !isVisible()) return;
      fired = true;
      reportExposure("interaction", "mobile navigation menu drawer opened");
      stop();
    }
    obs = new MutationObserver(check);
    obs.observe(panel, {
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden", "open"],
    });
    poll = setInterval(check, 500);
    setTimeout(stop, 120000);
    check();
  }

  function boot() {
    start();
    startExposure();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
